"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { useAnimatedRating } from "./hooks";
const AccessibilityLabel = {
  VERY_POOR: "Very Poor",
  POOR: "Poor",
  FAIR: "Fair",
  Good: "Good",
  EXCELLENT: "Excellent"
};
const AccessibilityLevels = [{
  range: 0,
  label: AccessibilityLabel.VERY_POOR,
  color: "#F59E0B"
}, {
  range: 25,
  label: AccessibilityLabel.POOR,
  color: "#6A5ACD"
}, {
  range: 50,
  label: AccessibilityLabel.FAIR,
  color: "#1E5EFF"
}, {
  range: 75,
  label: AccessibilityLabel.Good,
  color: "#00BE52"
}, {
  range: 100,
  label: AccessibilityLabel.EXCELLENT,
  color: "#059669"
}];
export const getAccessibilityLevel = score => {
  // Handle invalid inputs
  if (score == null || Number.isNaN(Number(score))) {
    return {
      label: "N/A",
      color: "#9CA3AF",
      score: 0
    };
  }
  const normalizedScore = Math.max(0, Math.min(100, Number(score)));
  // Find matching level (highest min ≤ score)
  let matchedLevel = AccessibilityLevels[4];
  if (score >= 100) {
    matchedLevel = AccessibilityLevels[4];
  } else if (score >= 75) {
    matchedLevel = AccessibilityLevels[3];
  } else if (score >= 50) {
    matchedLevel = AccessibilityLevels[2];
  } else if (score >= 25) {
    matchedLevel = AccessibilityLevels[1];
  } else {
    matchedLevel = AccessibilityLevels[0];
  }
  return {
    label: matchedLevel.label,
    color: matchedLevel.color,
    score: normalizedScore
  };
};

// Accessibility Circle Component
export const AccessibilityCircle = t0 => {
  const $ = _c(16);
  const {
    score,
    shouldAnimate: t1
  } = t0;
  const shouldAnimate = t1 === undefined ? true : t1;
  const {
    displayValue,
    animatedProgress
  } = useAnimatedRating(score, shouldAnimate);
  const animatedPercentage = animatedProgress;
  const animatedRotation = animatedPercentage / 100 * 360;
  let t2;
  if ($[0] !== score) {
    t2 = getAccessibilityLevel(score);
    $[0] = score;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const bgColor = t2.color;
  const t3 = `${bgColor}30`;
  let t4;
  if ($[2] !== t3) {
    t4 = <div className="absolute inset-0 rounded-full" style={{
      border: "5px solid",
      borderColor: t3,
      transition: "border-color 0.6s ease-in-out"
    }} />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = `conic-gradient(from -90deg, ${bgColor} 0deg, ${bgColor} ${animatedRotation}deg, transparent ${animatedRotation}deg)`;
  let t6;
  if ($[4] !== t5) {
    t6 = <div className="absolute inset-0 rounded-full" style={{
      background: t5,
      clipPath: "circle(50% at 50% 50%)",
      WebkitClipPath: "circle(50% at 50% 50%)"
    }} />;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="absolute rounded-full" style={{
      width: "calc(100% - 10px)",
      height: "calc(100% - 10px)",
      top: "5px",
      left: "5px",
      backgroundColor: "white"
    }} />;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== bgColor) {
    t8 = {
      color: bgColor,
      transition: "color 0.6s ease-in-out"
    };
    $[7] = bgColor;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== displayValue || $[10] !== t8) {
    t9 = <div className="absolute inset-0 flex items-center justify-center z-10"><span className="text-2xl font-bold font-poppins" style={t8}>{displayValue}%</span></div>;
    $[9] = displayValue;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t4 || $[13] !== t6 || $[14] !== t9) {
    t10 = <div className="relative w-32 h-32">{t4}{t6}{t7}{t9}</div>;
    $[12] = t4;
    $[13] = t6;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
};

// Accessibility Level Indicator Component
export const AccessibilityLevelIndicator = t0 => {
  const $ = _c(18);
  const {
    currentScore
  } = t0;
  const t1 = currentScore >= 0 && currentScore < 25;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      range: "0",
      label: "Very Poor",
      color: "#F59E0B",
      active: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = currentScore >= 25 && currentScore < 50;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      range: "25",
      label: "Poor",
      color: "#6A5ACD",
      active: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = currentScore >= 50 && currentScore < 75;
  let t6;
  if ($[4] !== t5) {
    t6 = {
      range: "50",
      label: "Fair",
      color: "#1E5EFF",
      active: t5
    };
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = currentScore >= 75 && currentScore < 100;
  let t8;
  if ($[6] !== t7) {
    t8 = {
      range: "75",
      label: "Good",
      color: "#00BE52",
      active: t7
    };
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = currentScore >= 100;
  let t10;
  if ($[8] !== t9) {
    t10 = {
      range: "100",
      label: "Excellent",
      color: "#059669",
      active: t9
    };
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== t10 || $[11] !== t2 || $[12] !== t4 || $[13] !== t6 || $[14] !== t8) {
    t11 = [t2, t4, t6, t8, t10];
    $[10] = t10;
    $[11] = t2;
    $[12] = t4;
    $[13] = t6;
    $[14] = t8;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const levels = t11;
  let t12;
  if ($[16] !== levels) {
    t12 = <div className="space-y-3 ml-10">{levels.map(_temp)}</div>;
    $[16] = levels;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  return t12;
};
function _temp(level, index) {
  return <div key={index} className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${level.active ? "bg-gray-50 border border-gray-200" : ""}`}><div className="w-3 h-3 rounded-full flex-shrink-0" style={{
      backgroundColor: level.color
    }} /><div className="flex-1"><span className="text-sm font-semibold text-gray-700 font-poppins">{level.range}</span><span className="text-base font-normal text-gray-700 font-poppins ml-2">{level.label}</span></div></div>;
}